<template>
  <div class="flex flex-col min-h-screen items-center">
    <div
      v-if="showBanner"
      class="flex justify-between items-center bg-white shadow-sm rounded-lg opacity-100 w-full"
    >
      <div class="flex-1 flex flex-col sm:flex-row items-center justify-center mt-3 mb-4 ml-4">
        <div class="flex sm:items-start">
          <img
            class="h-6 mr-1"
            src="@/assets/svg/icon_24px_info_green.svg"
            alt=""
            aria-hidden="true"
          >
          <span class="flex-wrap">Discover the new Passkey login experience.<a
            class="ml-2 b1 text-zyxel-green-700"
            href="https://community.zyxel.com/en/discussion/30569/passkeys-a-seamless-passwordless-login-to-your-zyxel-account/p1?new=1"
            target="_blank"
          >Learn more.</a></span>
        </div>
      </div>
      <button
        class="top-0 right-0 mr-4"
        @click="showBanner = false"
      >
        <img
          src="@/assets/svg/icon_48px_close.svg"
          width="24"
          height="24"
          alt="Close Banner"
        >
      </button>
    </div>
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="ml-4 sm:ml-0">
        <LogoIcon />
      </div>
      <div class="h-12 mt-5 mb-5 mr-4 sm:mr-8">
        <LanguageSwitch />
      </div>
    </div>
    <div class="max-w-[460px] w-full text-center flex-grow px-4">
      <h2 class="mt-5 mt-10 mb-5 text-center">
        {{ $t("messages.sign_in.title") }}
      </h2>

      <GoogleLogin
        :callback="callback"
        :error="callback"
        class="w-full"
      >
        <div
          class="social_btn flex items-center text-center mb-4 cursor-pointer"
          role="button"
          tabindex="0"
        >
          <div class="h-8 w-8 mt-2 mb-2 ml-4">
            <img
              src="@/assets/svg/icon_48px_Google.svg"
              alt="Google"
              aria-hidden="true"
            >
          </div>
          <span class="flex-grow text-center">{{ $t("messages.sign_in.google_login") }}</span>
        </div>
      </GoogleLogin>

      <div
        class="social_btn flex items-center text-center mb-4 cursor-pointer"
        role="button"
        tabindex="0"
        @click="triggerAppleSignIn"
        @keydown.enter="triggerAppleSignIn"
        @keydown.space="triggerAppleSignIn"
      >
        <div
          id="appleid-signin"
          hidden
          data-mode="logo-only"
          data-color="white"
          data-border="false"
          data-size="32"
          class="h-8 w-8 mt-2 mb-2 ml-4"
        />
        <div class="h-8 w-8 mt-2 mb-2 ml-4">
          <img
            src="@/assets/svg/icon_48px_Apple.svg"
            alt="Apple"
            aria-hidden="true"
          >
        </div>
        <span class="flex-grow text-center">{{ $t("messages.sign_in.apple_login") }}</span>
      </div>

      <div
        class="social_btn flex items-center text-center mb-4 cursor-pointer w-full"
        role="button"
        tabindex="0"
        @click="entraSignin"
        @keydown.enter="entraSignin"
        @keydown.space="entraSignin"
      >
        <div class="h-8 w-8 mt-2 mb-2 ml-4">
          <img
            src="@/assets/svg/icon_48px_Microsoft.svg"
            alt="Microsoft Entra ID"
            aria-hidden="true"
          >
        </div>
        <span class="flex-grow text-center">{{ $t("messages.sign_in.entra_login") }}</span>
      </div>

      <div
        class="social_btn flex items-center text-center cursor-pointer w-full"
        role="button"
        tabindex="0"
        @click="handlePasskeySignIn"
        @keydown.enter="handlePasskeySignIn"
        @keydown.space="handlePasskeySignIn"
      >
        <div class="h-8 w-8 mt-2 mb-2 ml-4">
          <img
            src="@/assets/svg/icon_32px_passkey.svg"
            alt="Passkey"
            aria-hidden="true"
          >
        </div>
        <span class="flex-grow text-center">{{ $t("messages.sign_in.passkey_login") }}</span>
      </div>
      <span
        v-if="errors.passkey_login"
        class="error-message mt-1 b2 block text-left w-full"
        aria-live="assertive"
      >{{ $t(errors.passkey_login) }}</span>
      <div
        class="interval mt-6"
        aria-hidden="true"
      >
        <div class="separator" />
        <div
          class="text-base"
          style="color: #575757"
        >
          {{ $t("messages.common.or") }}
        </div>
        <div class="separator" />
      </div>

      <div class="mb-6">
        <label
          for="user_email"
          class="b2 block text-left w-full mb-2 text-zyxel-gray-800"
        >{{ $t("messages.text_input.email")
        }}</label>
        <input
          id="user_email"
          v-model="userInput.email"
          type="text"
          inputmode="email"
          autocomplete="email"
          :class="['text-input pt-4 pl-4 pb-4 text-left h-12 w-full mb-4', { 'error-input': errors.email }]"
          placeholder="name@example.com"
          aria-labelledby="user_email"
          @focus="userInput.showPassword = true"
          @keyup.enter="handleSubmit"
        >
        <div :style="userInput.showPassword ? { height: 'auto', opacity: 1 } : { height: '0px', opacity: 0, overflow: 'hidden' }">
          <label
            for="user_password"
            class="b2 block text-left w-full mb-2"
            style="color: #575757"
          >{{
            $t("messages.text_input.password") }}</label>
          <PasswordInput
            id="user_password"
            v-model="userInput.password"
            :error="errors.password"
            autocomplete="current-password"
            @keyup.enter="handleSubmit"
          />
          <span
            v-if="(errors.email || errors.password)"
            class="error-message mt-1 b2 block text-left w-full"
            aria-live="assertive"
          >{{
            $t("messages.error_msg.invalid_email_password") }}</span>
          <div class="flex justify-between items-center mt-5 mb-6">
            <div class="flex items-center mycheckbox">
              <label
                for="remember_me"
                class="flex items-center cursor-pointer"
              >
                <input
                  id="remember_me"
                  v-model="userInput.rememberMe"
                  type="checkbox"
                  class="opacity-0 absolute h-8 w-8"
                >
                <div
                  :class="[{ 'border-zyxel-green-500': userInput.rememberMe, 'border-gray-800': !userInput.rememberMe }, 'border-2 w-6 h-6 rounded-md']"
                />
                <span class="ml-2 b1 text-zyxel-gray-800">{{ $t("messages.sign_in.checkbox_remember_me")
                }}</span>
              </label>
            </div>
            <router-link
              class="b1 text-zyxel-green-700"
              :to="{ name: 'ForgetPassword' }"
            >
              {{
                $t("messages.sign_in.link_recover_account") }}
            </router-link>
          </div>
        </div>
        <button
          class="button-primary w-full"
          @click="handleSubmit"
        >
          {{ $t("messages.button.sign_in") }}
        </button>
      </div>
      <div class="mb-6">
        <span
          class="mr-4 b1 text-zyxel-gray-800"
          style="color: #575757"
        >{{ $t("messages.common.hint_new_user")
        }}</span><router-link
          class="b1 text-zyxel-green-700"
          :to="{ name: 'SignUp' }"
        >
          {{
            $t("messages.common.link_create_account") }}
        </router-link>
      </div>

      <div
        class="interval mb-6"
        aria-hidden="true"
      >
        <div class="separator" />
        <div
          class="text-base"
          style="color: #575757"
        >
          {{ $t("messages.common.or") }}
        </div>
        <div class="separator" />
      </div>

      <div class="mb-6">
        <button
          class="button-secondary w-full"
          @click="loginDemo"
        >
          {{ $t("messages.button.try_demo") }}
        </button>
      </div>

      <div class="mb-6">
        <span
          class="mr-4 b1"
          style="color: #575757"
        >{{ $t("messages.common.hint_not_activated") }}</span><br>
        <router-link
          class="b1 text-zyxel-green-700"
          :to="{ name: 'SendActivation' }"
        >
          {{ $t("messages.common.link_resent_activation") }}
        </router-link>
      </div>
    </div>
    <ZyxelFooter />
    <SwitchAccount
      :is-visible="dialogStates.switchAccount"
      :login_method="getLoginMethod"
      @confirm="handleSwitchAccount"
      @cancel="handleCancel"
    />
    <AppleSigninFailDialog
      :is-show="dialogStates.appleFail"
      @close="close"
    />
    <EntraTroubleDialog
      :is-show="dialogStates.entraTrouble"
      @close="closeEntraTrobule"
    />
    <Loading
      v-model:active="isLoading"
      color="#3C9F00"
      loader="spinner"
      background-color="#FFFFFF"
      :is-full-page="true"
    />
    <EntraUnauthDialog
      :is-show="dialogStates.entraUnauth"
      @close="closeEntraUnauthDialog"
    />
    <PasskeyPromptDialog
      :is-show="dialogStates.passkeyPrompt"
      @ok="closePasskeyPromptDialog"
    />
  </div>
</template>

<script setup>
/* global grecaptcha */
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { useI18nStore } from '@/stores/i18nStore';
import Cookies from 'js-cookie';
import { googleOneTap } from 'vue3-google-login';
import { apiBaseUrl, appleClientId, appleRedirectUri, recaptchaSitekey, recaptchaVerifyFlag, passkeyRPId } from '@/config.js';
import { getSupportedLanguageOrDefault } from '@/config/languages.js';
import { base64urlDecode, arrayBufferToBase64url } from '@/utils/passkeyUtils';
import { logger } from '@/utils/logger';
import LogoIcon from '@/components/LogoIcon.vue';
import LanguageSwitch from '@/components/LanguageSwitch.vue';
import AppleSigninFailDialog from '@/components/AppleSignInFailDialog.vue';
import EntraTroubleDialog from '@/components/EntraTroubleDialog.vue';
import EntraUnauthDialog from '@/components/EntraUnauthDialog.vue';
import ZyxelFooter from '@/components/ZyxelFooter.vue';
import SwitchAccount from '@/components/SwitchAccount.vue';
import PasswordInput from '@/components/PasswordInput.vue';
import PasskeyPromptDialog from '@/components/PasskeyPromptDialog.vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

const loadScript = (src, callback) => {
    if (!document.head.querySelector(`script[src="${src}"]`)) {
        let script = document.createElement('script');
        script.onload = callback;
        script.src = src;
        script.setAttribute('async', 'true');
        script.setAttribute('defer', 'true');
        document.head.appendChild(script);
    } else {
        callback();
    }
}

// 關鍵腳本並行載入 - 在組件初始化之前立即開始
const scriptLoadingState = ref({
    recaptcha: false,
    apple: false
});

const criticalScripts = [
    {
        src: "https://www.google.com/recaptcha/enterprise.js?render=" + recaptchaSitekey,
        key: "recaptcha"
    },
    {
        src: "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
        key: "apple"
    }
];

// Apple Sign-In 初始化函數
const initializeAppleSignIn = () => {
    if (window.AppleID) {
        window.AppleID.auth.init({
            clientId: appleClientId,
            scope: "name email",
            redirectURI: appleRedirectUri,
            nonce: "[NONCE]",
            usePopup: false
        });
    }
}

// 立即開始載入關鍵腳本，不等任何 Vue 生命週期
criticalScripts.forEach(({ src, key }) => {
    loadScript(src, () => {
        scriptLoadingState.value[key] = true;

        // 如果是 Apple 腳本載入完成，立即初始化
        if (key === "apple") {
            initializeAppleSignIn();
        }
    });
});

const auth = useAuthStore();
const { locale } = useI18n();
const i18nStore = useI18nStore();

const userInput = ref({
    email: "",
    password: "",
    rememberMe: false,
    showPassword: false
});

const errors = ref({
    email: false,
    password: false,
    passkey_login: "",
});
const router = useRouter();

let isSubmitting = false;
const dialogStates = ref({
    switchAccount: false,
    appleFail: false,
    entraTrouble: false,
    entraUnauth: false,
    passkeyPrompt: false
});
const isLoading = ref(false);
const getLoginMethod = ref("google");
const showBanner = ref(true);

const changeLanguage = async (newLocale) => {
    await i18nStore.loadLanguage(newLocale);
    locale.value = newLocale;
    Cookies.set("v3_locale", newLocale);
};

// 統一的認證後處理函數
const authenticateAndUpdateLanguage = async (accessToken) => {
    await auth.authenticateUser(accessToken);
    if (auth.current_user?.display_language) {
        const supportedLanguage = getSupportedLanguageOrDefault(auth.current_user.display_language);
        await changeLanguage(supportedLanguage);
    }
};

// 統一的 loading 管理函數
const withLoading = async (asyncFn) => {
    isLoading.value = true;
    try {
        return await asyncFn();
    } finally {
        isLoading.value = false;
    }
};

// 統一的錯誤處理函數
const handleError = (error, options = {}) => {
    const {
        message = "Error:",
        resetLoading = true,
        closeDialog = false,
        setEmailError = false,
        setPasswordError = false
    } = options;

    logger.error(message, error);

    if (resetLoading) {
        isLoading.value = false;
    }

    if (closeDialog) {
        dialogStates.value.switchAccount = false;
    }

    if (setEmailError) {
        errors.value.email = true;
    }

    if (setPasswordError) {
        errors.value.password = true;
    }
};

const saveCurrentLocale = () => {
    const currentLocale = locale.value;
    localStorage.setItem("before_idp_locale", currentLocale);
}

const handleOAuthResponse = async (endpoint, payload) => {
    await withLoading(async () => {
        try {
            const login_time = new Date().toString();
            const axiosResponse = await axios.post(endpoint, { ...payload, login_time });

            const { status, access_token } = axiosResponse.data;

            if (access_token) {
                await authenticateAndUpdateLanguage(access_token);
            }

            await handleAuthStatus(status);

        } catch (error) {
            handleError(error, {
                message: "OAuth Error:",
                resetLoading: false
            });
            throw error;
        }
    });
};

const handleAuthStatus = async (status) => {
    const statusActions = {
        switch: () => {
            dialogStates.value.switchAccount = true;
            getLoginMethod.value = "google";
        },
        entra_block: () => {
            dialogStates.value.entraTrouble = true;
        },
        newuser: async () => {
            const beforeLocale = localStorage.getItem("before_idp_locale");
            if (beforeLocale) {
                await auth.updateUserLanguage({ displayLanguage: beforeLocale });
                locale.value = beforeLocale;
            }
            auth.getAuthorizationCodeAndRedirect();
            localStorage.removeItem("rememberedEmail");
        },
        login: () => {
            auth.getAuthorizationCodeAndRedirect();
            localStorage.removeItem("rememberedEmail");
        }
    };

    await statusActions[status]?.();
};

const callback = async (response) => {
    saveCurrentLocale();

    if (response.code) {
        await handleOAuthResponse(`${apiBaseUrl}/api/oauth/google/callback`, { code: response.code });
    }

    if (response.credential) {
        await handleOAuthResponse(`${apiBaseUrl}/api/oauth/google/one_tap`, { token: response.credential });
    }
};

const triggerAppleSignIn = () => {
    // 保存当前语言设置
    saveCurrentLocale();

    const appleSignInButton = document.getElementById("appleid-signin");
    if (appleSignInButton) {
        appleSignInButton.click();
    }
};

const entraSignin = () => {
    isLoading.value = false;
    router.push({ name: "EntraSignIn" });
};

// Passkey 錯誤映射表 - 消除硬編碼
const PASSKEY_ERROR_MAP = {
    94518: 'messages.error_msg.credential_not_found',
    94515: 'messages.error_msg.invalid_challenge',
    94522: 'messages.error_msg.device_no_uv',
    94500: 'messages.error_msg.passkey_login_fail'
};

// 構建 Passkey 驗證 payload
const buildVerificationPayload = (credential) => {
    return {
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        response: {
            clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
            authenticatorData: arrayBufferToBase64url(credential.response.authenticatorData),
            signature: arrayBufferToBase64url(credential.response.signature),
            userHandle: credential.response.userHandle ? arrayBufferToBase64url(credential.response.userHandle) : null
        },
        type: credential.type,
        clientExtensionResults: credential.clientExtensionResults ?? {}
    };
};

// 處理 Credential Not Found 錯誤
const handleCredentialNotFound = async (credentialId) => {
    if (!credentialId) {
        logger.warn('credential_id is undefined for Credential not found.');
        return;
    }

    try {
        if (window.PublicKeyCredential && PublicKeyCredential.signalUnknownCredential) {
            /* global PublicKeyCredential */
            await PublicKeyCredential.signalUnknownCredential({
                rpId: passkeyRPId,
                credentialId: credentialId
            });
        } else {
            logger.warn('PublicKeyCredential.signalUnknownCredential is not supported in this browser.');
        }
    } catch (e) {
        logger.warn('signalUnknownCredential failed or not supported:', e);
    }
};

// 統一的 Passkey 錯誤處理
const handlePasskeyError = async (error) => {
    logger.error('Error signin verify passkey:', error);

    const rawDetail = error?.response?.data?.detail;
    const code = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.error_code ?? 94500
        : 94500;
    const credentialId = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.credential_id ?? null
        : null;

    // 設置錯誤訊息
    errors.value.passkey_login = PASSKEY_ERROR_MAP[code] || PASSKEY_ERROR_MAP[94500];

    // 處理特殊錯誤：Credential not found
    if (code === 94518) {
        await handleCredentialNotFound(credentialId);
    }
};

const handlePasskeySignIn = async () => {
    errors.value.passkey_login = "";

    await withLoading(async () => {
        try {
            // 初始化 Passkey 登入
            const response = await axios.post(`${apiBaseUrl}/api/passkey/signin`);
            const options = response.data;
            options.challenge = base64urlDecode(options.challenge);

            // 獲取 credential
            const credential = await navigator.credentials.get({ publicKey: options });
            if (!credential) {
                errors.value.passkey_login = "messages.error_msg.passkey_login_fail";
                return;
            }

            // 驗證 passkey
            const verificationPayload = buildVerificationPayload(credential);
            const payload = {
                verify_time: new Date().toString(),
                credential: verificationPayload
            };

            const verificationResponse = await axios.post(`${apiBaseUrl}/api/passkey/signin/verify`, payload);

            // 處理成功登入
            const accessToken = verificationResponse.data.access_token;
            await authenticateAndUpdateLanguage(accessToken);
            auth.getAuthorizationCodeAndRedirect();

        } catch (error) {
            await handlePasskeyError(error);
        }
    });

    // 重置提交狀態
    isSubmitting = false;
}

const handleSignIn = async () => {
    try {
        const currentTime = new Date().toString();  // local time
        const response = await axios.post(`${apiBaseUrl}/api/users/login`, {
            email: userInput.value.email.trim(),
            password: userInput.value.password,
            login_time: currentTime,  // 登入的時間
        });

        const accessToken = response.data.access_token;
        await authenticateAndUpdateLanguage(accessToken);

        if (userInput.value.rememberMe) {
            localStorage.setItem("rememberedEmail", userInput.value.email.trim());
        } else {
            localStorage.removeItem("rememberedEmail");
        }
        if (response.data.two_factor_auth) {
            const authType = response.data.auth_type;
            if (authType === 0) {
                isLoading.value = false;
                router.push({ name: "Otp2FA" });
            } else if (authType === 1) {
                isLoading.value = false;
                auth.send2FASigninEmail();
                // Redirect to Email2FA
                router.push({ name: "Email2FA", query: { to_email: userInput.value.email.trim() } });
            }
            return; // Stop further execution after redirection
        }

        // Check passwordless_status and show prompt dialog (once per day)
        if (auth.current_user?.passwordless_status === 'not_ready') {
            const lastPromptTimestamp = localStorage.getItem('passkeyPromptTimestamp');
            const now = Date.now();
            const ONE_DAY_MS = 24 * 60 * 60 * 1000;

            const shouldPrompt = !lastPromptTimestamp || (now - parseInt(lastPromptTimestamp, 10) > ONE_DAY_MS);

            if (shouldPrompt) {
                isLoading.value = false;
                dialogStates.value.passkeyPrompt = true;
                localStorage.setItem('passkeyPromptTimestamp', now.toString());
                isSubmitting = false;
                return; // 顯示 dialog，不跳轉
            }
        }

        auth.getAuthorizationCodeAndRedirect();
        isSubmitting = false;
    } catch (error) {
        handleError(error, {
            setEmailError: true,
            setPasswordError: true
        });
        isSubmitting = false;
    }
}

const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!userInput.value.email || !userInput.value.password) {
        errors.value.email = !userInput.value.email;
        errors.value.password = !userInput.value.password;
        return;
    }

    isSubmitting = true;
    isLoading.value = true;
    let action_event = "signin";

    // 啟動 reCAPTCHA 並執行驗證
    grecaptcha.enterprise.ready(async () => {
        const token = await grecaptcha.enterprise.execute(
            recaptchaSitekey, { action: action_event }
        );
        // 將驗證結果提交到後端伺服器
        axios.post(`${apiBaseUrl}/api/recaptcha/token`, {
            token: token,
            action: action_event
        }).then(response => {
            logger.log("score:", response.data.score);
            logger.log("reason:", response.data.reason);

            if (recaptchaVerifyFlag) {
                const success = response.data.success;
                const score = response.data.score;
                if (success && score > 0.5) {
                    handleSignIn(); // 驗證成功後的處理函式
                } else {
                    isLoading.value = false;
                    isSubmitting = false;
                    router.push({ name: "RobotEndpoint" });
                }
            }
            else {
                handleSignIn(); // 驗證成功後的處理函式
            }
        })
            .catch(error => {
                logger.error("Error:", error);
                isLoading.value = false;
                isSubmitting = false;
            });
    });
};

const loginDemo = async () => {
    await withLoading(async () => {
        try {
            const response = await axios.post(`${apiBaseUrl}/api/users/demo`);
            await authenticateAndUpdateLanguage(response.data.access_token);
            auth.getAuthorizationCodeAndRedirect();
        } catch (error) {
            handleError(error, {
                closeDialog: true,
                resetLoading: false // withLoading 會處理
            });
            throw error; // 讓 withLoading 知道有錯誤發生
        }
    });
};

const handleSwitchAccount = async () => {
    const login_method = getLoginMethod.value.toLowerCase();
    dialogStates.value.switchAccount = false;

    await withLoading(async () => {
        try {
            const login_time = new Date().toString();  // local time
            const response = await axios.post(`${apiBaseUrl}/api/users/login/switch`, { login_method, login_time });
            localStorage.removeItem("rememberedEmail");
            await authenticateAndUpdateLanguage(response.data.access_token);
            auth.getAuthorizationCodeAndRedirect();
        } catch (error) {
            handleError(error, {
                closeDialog: true,
                resetLoading: false
            });
            throw error;
        }
    });
}

const handleCancel = () => {
    const auth = useAuthStore();
    dialogStates.value.switchAccount = false;
    auth.logout();
}

const close = () => {
    dialogStates.value.appleFail = false;
}

const closeEntraUnauthDialog = () => {
    dialogStates.value.entraUnauth = false;
};

const closeEntraTrobule = () => {
    dialogStates.value.entraTrouble = false;
}

const closePasskeyPromptDialog = async () => {
    dialogStates.value.passkeyPrompt = false;
    auth.getAuthorizationCodeAndRedirect();
};

// 查詢參數路由表 - 消除所有特殊情況的統一邏輯
const queryStateRouters = {
        "entra_block_apple": () => (dialogStates.value.entraTrouble = true),
        "unauth_entra": () => (dialogStates.value.entraUnauth = true),
        "switch_apple": () => (dialogStates.value.switchAccount = true),
        "login_apple": (authStore, status) => handleAppleNotifyLogin(authStore, status),
        "newuser_apple": (authStore, status) => handleAppleNotifyLogin(authStore, status),
        "login_entra": async (authStore, token, status) => {
            await processEntraLogin(authStore, token, status);
        },
        "newuser_entra": async (authStore, token, status) => {
            await processEntraLogin(authStore, token, status);
        }
};

const processEntraLogin = async (authStore, token, status) => {
    if (!token) {
        logger.error("API response does not contain token:", router.currentRoute.value.query);
        return false;
    }

    await authStore.authenticateUser(token);
    if (status === "newuser") {
        await setLanguageFromLocalStorage(authStore);
    }
    authStore.getAuthorizationCodeAndRedirect();
    localStorage.removeItem("rememberedEmail");
    return true;
};

const checkQueryParams = async () => {
    await withLoading(async () => {
        const authStore = useAuthStore();
        const { fail_msg, token, status, login_method } = router.currentRoute.value.query;

        // 處理失敗訊息 - 早期返回
        if (fail_msg) {
            getLoginMethod.value = "Apple";
            dialogStates.value.appleFail = true;
            return;
        }

        // 處理特殊狀態 - 需要早期返回的情況
        const earlyReturnStates = ["entra_block_apple", "unauth_entra"];
        const routerKey = `${status}_${login_method}`;

        if (earlyReturnStates.includes(routerKey)) {
            queryStateRouters[routerKey]();
            return;
        }

        // 處理認證
        if (token) {
            getLoginMethod.value = "Apple";
            await authStore.authenticateUser(token);
        }else{
            googleOneTap({ cancelOnTapOutside: true })
                .then(callback)
                .catch((error) => {
                    logger.log("Handle the error", error);
                });
            return;
        }

        if (!authStore.isAuthenticated) return;

        // 處理需要認證後的狀態路由
        const postAuthStates = ["switch_apple", "login_apple", "newuser_apple", "login_entra", "newuser_entra"];
        if (postAuthStates.includes(routerKey)) {
            const stateRouter = queryStateRouters[routerKey];
            await stateRouter(authStore, token, status);
            return;
        }

        // 檢查 web session 登入狀態
        const loginInToken = localStorage.getItem("user-token");
        if (loginInToken) {
            const loginStatus = await authStore.checkLoginStatus(loginInToken);
            if (!loginStatus) {
                authStore.purgeUser();
            }
        }
    });
};


const handleAppleNotifyLogin = async (authStore, status) => {
    try {
        const login_time = new Date().toString();
        const response = await axios.post(`${apiBaseUrl}/api/users/login/apple_notify`, {
            login_method: "apple",
            login_time
        });

        if (response.data?.access_token) {
            await authenticateAndUpdateLanguage(response.data.access_token);
        } else {
            logger.error("API response does not contain access_token:", response.data);
            return;
        }

        if (status === "newuser") {
            await setLanguageFromLocalStorage(authStore);
        }

        authStore.getAuthorizationCodeAndRedirect();
        localStorage.removeItem("rememberedEmail");
    } catch (error) {
        handleError(error, { message: "Error during API call:" });
    }
};


const setLanguageFromLocalStorage = async (authStore) => {
    const beforeLocale = localStorage.getItem("before_idp_locale");
    if (beforeLocale) {
        const supportedLanguage = getSupportedLanguageOrDefault(beforeLocale);
        await authStore.updateUserLanguage({ displayLanguage: supportedLanguage });
        changeLanguage(supportedLanguage);
    }
};


onMounted(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
        userInput.value.email = rememberedEmail;
        userInput.value.showPassword = true;
        userInput.value.rememberMe = true;
    }

    // 確保 Apple Sign-In 已初始化（處理 SPA 路由切換後的情況）
    if (window.AppleID) {
        try {
            initializeAppleSignIn();
        } catch (error) {
            logger.warn('Apple Sign-In re-initialization attempt:', error);
        }
    }

    checkQueryParams();
});
</script>

<style>
.social_btn {
    /* Layout Properties */
    max-width: 460px;
    height: 48px;
    /* UI Properties */
    background: var(--unnamed-color-ffffff) 0% 0% no-repeat padding-box;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    box-shadow: 0px 0px 3px #00000029;
    border-radius: 8px;
    opacity: 1;
}

/* 其他樣式和響應式設計 */
.interval {
    color: var(--basic-text-color);
    display: flex;
    align-items: center;
    gap: 1.75rem;
}

.separator {
    background-color: #DDDDDD;
    width: 100%;
    height: 0.0625rem;
}
</style>
